import { createContext, useContext, useCallback, useState, useMemo, type ReactNode } from "react";

type ModalType = 
  | "confirm"
  | "report"
  | "share"
  | "contact"
  | "createWant"
  | "safety"
  | "custom";

interface ModalState {
  type: ModalType;
  isOpen: boolean;
  props?: Record<string, unknown>;
}

// Split into actions (stable) and state (changes)
interface ModalActionsContextType {
  openModal: (type: ModalType, props?: Record<string, unknown>) => void;
  closeModal: () => void;
}

interface ModalStateContextType {
  modal: ModalState | null;
  isOpen: (type: ModalType) => boolean;
}

const ModalActionsContext = createContext<ModalActionsContextType | null>(null);
const ModalStateContext = createContext<ModalStateContextType | null>(null);

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [modal, setModal] = useState<ModalState | null>(null);

  // Actions are stable - memoize once
  const actions = useMemo<ModalActionsContextType>(() => ({
    openModal: (type: ModalType, props?: Record<string, unknown>) => {
      setModal({ type, isOpen: true, props });
    },
    closeModal: () => {
      setModal(null);
    },
  }), []);

  // isOpen function needs current modal state
  const isOpen = useCallback(
    (type: ModalType) => modal?.type === type && modal.isOpen,
    [modal]
  );

  // Memoize state value
  const stateValue = useMemo<ModalStateContextType>(
    () => ({ modal, isOpen }),
    [modal, isOpen]
  );

  return (
    <ModalActionsContext.Provider value={actions}>
      <ModalStateContext.Provider value={stateValue}>
        {children}
      </ModalStateContext.Provider>
    </ModalActionsContext.Provider>
  );
}

// Stable actions hook - won't cause re-renders
export function useModalActions() {
  const context = useContext(ModalActionsContext);
  if (!context) {
    throw new Error("useModalActions must be used within a ModalProvider");
  }
  return context;
}

// State hook - re-renders when modal changes
export function useModalState() {
  const context = useContext(ModalStateContext);
  if (!context) {
    throw new Error("useModalState must be used within a ModalProvider");
  }
  return context;
}

// Combined hook for backwards compatibility
export function useModal() {
  const actions = useModalActions();
  const state = useModalState();
  return { ...state, ...actions };
}

// Convenience hooks for specific modals - optimized to use stable actions
export function useConfirmModal() {
  const { openModal, closeModal } = useModalActions();
  const { isOpen, modal } = useModalState();
  return useMemo(() => ({
    open: (props: { title: string; message: string; onConfirm: () => void; onCancel?: () => void }) =>
      openModal("confirm", props),
    close: closeModal,
    isOpen: isOpen("confirm"),
    props: modal?.type === "confirm" ? modal.props : undefined,
  }), [openModal, closeModal, isOpen, modal]);
}

export function useReportModal() {
  const { openModal, closeModal } = useModalActions();
  const { isOpen, modal } = useModalState();
  return useMemo(() => ({
    open: (props: { adId?: string; userId?: string }) => openModal("report", props),
    close: closeModal,
    isOpen: isOpen("report"),
    props: modal?.type === "report" ? modal.props : undefined,
  }), [openModal, closeModal, isOpen, modal]);
}

export function useShareModal() {
  const { openModal, closeModal } = useModalActions();
  const { isOpen, modal } = useModalState();
  return useMemo(() => ({
    open: (props: { url: string; title: string }) => openModal("share", props),
    close: closeModal,
    isOpen: isOpen("share"),
    props: modal?.type === "share" ? modal.props : undefined,
  }), [openModal, closeModal, isOpen, modal]);
}

export type { ModalType, ModalState };
